import { IClientPortalInfo, ClientInfoStatus } from '@common/interfaces/client'
import { IResponse } from '@common/interfaces/request'
import { UserRole } from '@common/interfaces/user'
import { Action } from 'redux'
import { put, select, takeEvery } from 'redux-saga/effects'
import { IApi } from '../../api'
import { ROUTES } from '../../constants/routes'
import { mapErrorToLoginErrorType } from '../../helpers/loginErrorMapping'
import { putPromise } from '../../helpers/redux'
import { writeStoredFlag } from '../../helpers/storedFlag'
import { clearMentionSearchCache } from '../../hooks/useMentionSuggestions'
import { getBanner } from '../clientInfo/actions'
import { getLoanBalanceStartDate } from '../loanBalance/actions'
import { sagaNavigate } from '../navigationHelper'
import { listLastNotifications } from '../notification/actions'
import { VERIFY_TWO_FACTOR_SUCCESS } from '../twoFactor/actions'
import {
  CHECK_SETUP_PASSWORD_FAILURE,
  LINK_GOOGLE_SUCCESS,
  UNLINK_GOOGLE_SUCCESS,
  LINK_MICROSOFT_SUCCESS,
  UNLINK_MICROSOFT_SUCCESS,
  loadClientInfo,
  loadProfile,
  LOGIN_AS_CLIENT_SUCCESS,
  LOGIN_SUCCESS,
  GOOGLE_AUTH_SUCCESS,
  GOOGLE_INVITATION_AUTH_SUCCESS,
  MICROSOFT_AUTH_SUCCESS,
  MICROSOFT_INVITATION_AUTH_SUCCESS,
  LOGOUT_AS_CLIENT_SUCCESS,
  SWITCH_CLIENT_SUCCESS,
  SESSION_EXPIRED,
  SIGN_OUT_ACTIONS,
  openSidebar,
  PERMISSION_DENIED,
  PROFILE_SUCCESS,
  SETUP_PASSWORD_SUCCESS,
  UPDATE_PASSWORD_SUCCESS,
  PROCESS_INVITATION_FAILURE,
  LOGIN_FAILURE,
  GOOGLE_AUTH_FAILURE,
  MICROSOFT_AUTH_FAILURE,
  GOOGLE_INVITATION_AUTH_FAILURE,
  MICROSOFT_INVITATION_AUTH_FAILURE,
  SETUP_PASSWORD_FAILURE,
  RESET_PASSWORD_FAILURE,
  PASSWORD_RECOVERY_FAILURE,
  SIDEBAR_PINNED_STORAGE_KEY,
  TOGGLE_SIDEBAR,
} from './actions'
import { clientInfo, isSidebarOpen } from './selectors'

export function* loginSuccess(api: IApi) {
  yield takeEvery(
    [
      LOGIN_SUCCESS,
      GOOGLE_AUTH_SUCCESS,
      GOOGLE_INVITATION_AUTH_SUCCESS,
      MICROSOFT_AUTH_SUCCESS,
      MICROSOFT_INVITATION_AUTH_SUCCESS,
      SETUP_PASSWORD_SUCCESS,
    ],
    function* (action: IResponse<{ jwt: string; requiresTwoFactor?: boolean }> & Action) {
      // Check if 2FA is required
      if (action.data.requiresTwoFactor) {
        // 2FA state will be handled by the login reducer/component
        return
      }

      // Normal login flow - set JWT token
      api.client.setToken(action.data.jwt)

      // Clear the justLoggedOut flag on successful login
      sessionStorage.removeItem('justLoggedOut')

      yield putPromise(loadProfile())
    },
  )
}

// Local teardown for both kinds of sign-out — the user asking to leave, and the server
// refusing our token. Only the server call differs between them, and that happens (or
// deliberately does not) in the action itself.
export function* logoutSuccess(api: IApi) {
  yield takeEvery(SIGN_OUT_ACTIONS, function (action: Action) {
    // A reactive sign-out gives up the shared token only if it is still this tab's own — see
    // ApiClient.removeToken. A deliberate one revokes the whole session server-side, so there
    // is nothing left for any tab to keep and storage goes with it.
    api.client.removeToken({ onlyIfCurrent: action.type === SESSION_EXPIRED })

    // Signing out is an SPA transition, not a reload, so module-level state
    // outlives the identity change. Mention results are permission-scoped (see
    // ListMentionSuggestions' peopleOnly narrowing), and the cache is keyed by
    // search text alone — so without this the next person to sign in on this
    // machine could be shown rows the server would have withheld from them.
    clearMentionSearchCache()

    // Set flag to prevent auto-select on Google One Tap after logout
    sessionStorage.setItem('justLoggedOut', 'true')

    sagaNavigate(ROUTES.LOGIN)
  })
}

export function* googleAuthLinking() {
  yield takeEvery(
    [
      LINK_GOOGLE_SUCCESS,
      UNLINK_GOOGLE_SUCCESS,
      LINK_MICROSOFT_SUCCESS,
      UNLINK_MICROSOFT_SUCCESS,
      UPDATE_PASSWORD_SUCCESS,
    ],
    function* () {
      yield putPromise(loadProfile())
    },
  )
}

export function* profileSuccess() {
  yield takeEvery(PROFILE_SUCCESS, function* (action: IResponse & Action) {
    const { role, isProspectUser } = action.data as {
      role: UserRole
      isProspectUser?: boolean
    }
    if (role === UserRole.CLIENT_USER) {
      yield put(openSidebar())
      yield putPromise(loadClientInfo())
      yield putPromise(getBanner())
    } else if (!isProspectUser && role !== UserRole.TASKS_AND_MESSAGING) {
      // The tasks-and-messaging role has no loan data and no permission to ask for
      // it; firing this on login would 403 and bounce them via the
      // permissionDenied saga below before they ever reached a page they do have.
      yield putPromise(getLoanBalanceStartDate())
    }
    if (role !== UserRole.PARTICIPANT_USER) {
      yield putPromise(listLastNotifications())
    }
  })
}

export function* permissionDenied() {
  yield takeEvery(PERMISSION_DENIED, function () {
    sagaNavigate(ROUTES.HOMEPAGE)
  })
}

// Only the user's own toggle is remembered. OPEN_SIDEBAR is the client portal forcing
// the rail open on login (including an admin logged in as a client), not a choice.
export function* toggleSidebarPersist() {
  yield takeEvery(TOGGLE_SIDEBAR, function* () {
    const isPinned = (yield select(isSidebarOpen)) as boolean
    writeStoredFlag(SIDEBAR_PINNED_STORAGE_KEY, isPinned)
  })
}

export function* loginAsClientSuccess(api: IApi) {
  yield takeEvery(
    [LOGIN_AS_CLIENT_SUCCESS],
    function* (action: IResponse<{ jwt: string }> & Action) {
      yield api.client.setToken(action.data.jwt)
      window.location.href = '/'
    },
  )
}

// A new company re-scopes the whole portal, so start it over rather than patch state.
export function* switchContextSuccess(api: IApi) {
  yield takeEvery([SWITCH_CLIENT_SUCCESS], function* (action: IResponse<{ jwt: string }> & Action) {
    yield api.client.setToken(action.data.jwt)
    window.location.href = '/'
  })
}

export function* logoutAsClientSuccess(api: IApi) {
  yield takeEvery(
    [LOGOUT_AS_CLIENT_SUCCESS],
    function* (action: IResponse<{ jwt: string }> & Action) {
      yield api.client.setToken(action.data.jwt)

      const info: IClientPortalInfo = yield select(clientInfo)
      if (info?.id) {
        window.location.href =
          info.clientStatus === ClientInfoStatus.DueDiligence
            ? `/due-diligence/${info.id}`
            : `/clients/${info.id}`
      } else {
        window.location.href = '/'
      }
    },
  )
}

export function* verify2FASuccess(api: IApi) {
  yield takeEvery(
    VERIFY_TWO_FACTOR_SUCCESS,
    function* (action: IResponse<{ jwt: string; isManage?: boolean }> & Action) {
      // Set the JWT token
      api.client.setToken(action.data.jwt)

      // Clear the justLoggedOut flag on successful login
      sessionStorage.removeItem('justLoggedOut')

      // Load profile
      if (!action.data.isManage) {
        yield putPromise(loadProfile())
      }
    },
  )
}

export const loginErrorActions = [
  LOGIN_FAILURE,
  GOOGLE_AUTH_FAILURE,
  MICROSOFT_AUTH_FAILURE,
  GOOGLE_INVITATION_AUTH_FAILURE,
  MICROSOFT_INVITATION_AUTH_FAILURE,
  SETUP_PASSWORD_FAILURE,
  RESET_PASSWORD_FAILURE,
  CHECK_SETUP_PASSWORD_FAILURE,
  PROCESS_INVITATION_FAILURE,
  PASSWORD_RECOVERY_FAILURE,
]

export function* loginFailure() {
  yield takeEvery(loginErrorActions, function (action: IResponse & Action) {
    const errorType = mapErrorToLoginErrorType(action.error)

    // Only redirect for AuthError codes, let others fall through to snackbar
    if (errorType === 'default') {
      return
    }

    // Flag to prevent UI saga from showing snackbar
    ;(window as Window & { __REDIRECTING_TO_LOGIN_ERROR?: boolean }).__REDIRECTING_TO_LOGIN_ERROR =
      true

    const errorMessage = action.error?.message

    // Build query parameters for error page
    const params = new URLSearchParams()
    params.set('error', errorType)
    if (errorMessage) {
      params.set('message', errorMessage)
    }

    // Redirect to login error page
    sagaNavigate(`${ROUTES.LOGIN_ERROR}?${params.toString()}`)

    // Reset flag after navigation
    setTimeout(() => {
      ;(
        window as Window & { __REDIRECTING_TO_LOGIN_ERROR?: boolean }
      ).__REDIRECTING_TO_LOGIN_ERROR = false
    }, 100)
  })
}
